import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  adminLogin,
  adminLogout,
  restoreSession,
  setAccessToken,
  setSessionExpiredHandler,
  type AdminAuthResponse,
} from '../api/client';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: AdminAuthResponse['user'] | null;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Holds { user, status } in memory only (never localStorage — ADR-0004/0005).
 * On boot, attempts POST /v1/admin/auth/refresh immediately: the HttpOnly
 * cookie, if present and valid, silently restores the session before any
 * route renders — this is the SPA's answer to "no server-side session."
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AdminAuthResponse['user'] | null>(null);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null);
      setStatus('unauthenticated');
    });
    restoreSession().then((restored) => {
      if (restored) {
        setAccessToken(restored.access_token);
        setUser(restored.user);
        setStatus('authenticated');
      } else {
        setStatus('unauthenticated');
      }
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await adminLogin(email, password);
    setAccessToken(res.access_token);
    setUser(res.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    await adminLogout();
    setAccessToken(null);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo(() => ({ status, user, login, logout }), [status, user, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() must be called within an AuthProvider');
  return ctx;
}
